import type { QueryClient } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";

import { MobileUnsupported } from "@/components/common/MobileUnsupported";
import { Providers } from "@/app/providers";
import { chatInfiniteRouteQuery, ensureProjectRouteData, projectsListRouteQuery } from "@/lib/routeQueries";

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: () => (
    <>
      <MobileUnsupported />
      <div className="hidden md:contents">
        <Providers>
          <Outlet />
        </Providers>
      </div>
    </>
  ),
});

// ---------------------------------------------------------------------------
// Index → redirect to /assistant
// ---------------------------------------------------------------------------

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/assistant" });
  },
});

// ---------------------------------------------------------------------------
// Login (reuses existing page component)
// ---------------------------------------------------------------------------

import LoginPage from "@/app/login/page";

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "login",
  component: LoginPage,
});

// ---------------------------------------------------------------------------
// Authenticated layout
// ---------------------------------------------------------------------------

import { AuthenticatedLayout } from "@/pages/AuthenticatedLayout";

const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authenticated",
  component: AuthenticatedLayout,
});

// ---------------------------------------------------------------------------
// Assistant routes (reuse existing page components)
// ---------------------------------------------------------------------------

import { ChatSurface } from "@/components/assistant/ChatSurface";
import { ChatsPageClient } from "@/app/(pages)/chats/ChatsPageClient";
import { ChatSkeleton } from "@/components/skeletons/ChatSkeleton";
import { RouteErrorBoundary, RouteErrorFallback } from "@/components/common/RouteErrorBoundary";

const assistantRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "assistant",
  component: () => <ChatSurface />,
});

const chatsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "chats",
  component: ChatsPageClient,
});

const assistantChatRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "assistant/chat/$id",
  loader: ({ context: { queryClient }, params: { id } }) =>
    queryClient.ensureInfiniteQueryData(chatInfiniteRouteQuery(id)),
  pendingComponent: ChatSkeleton,
  component: AssistantChatRouteBoundary,
});

function AssistantChatRouteBoundary() {
  const { id } = assistantChatRoute.useParams();
  return (
    <RouteErrorBoundary fallback={(error) => <RouteErrorFallback error={error} message="Could not load chat." />} resetKey={id}>
      <Suspense fallback={<ChatSkeleton />}>
        <AssistantChatWrapper />
      </Suspense>
    </RouteErrorBoundary>
  );
}

function AssistantChatWrapper() {
  // Inline wrapper to extract params and delegate to the existing page component.
  // The page component already lives at the Next.js path — we import and reuse it.
  const { id } = assistantChatRoute.useParams();
  const { setCurrentChatId } = useChatHistory();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useSuspenseInfiniteQuery(chatInfiniteRouteQuery(id));
  useEffect(() => { setCurrentChatId(id); }, [id, setCurrentChatId]);
  const pages = data.pages;
  const newestPage = pages[0];
  const rows = [...pages].reverse().flatMap((page) => page.messages);
  return <ChatSurface chatId={id} initialTitle={newestPage?.chat.title} initialRows={rows} initialLive={newestPage?.live} hasOlderMessages={!!hasNextPage} isLoadingOlderMessages={isFetchingNextPage} onLoadOlderMessages={() => void fetchNextPage()} />;
}

// ---------------------------------------------------------------------------
// Projects routes
// ---------------------------------------------------------------------------

import { ProjectsPageClient } from "@/app/(pages)/projects/ProjectsPageClient";
import { ProjectPageClient } from "@/app/(pages)/projects/[id]/ProjectPageClient";
import { ProjectSkeleton } from "@/components/skeletons/ProjectSkeleton";
import { ProjectsPageSkeleton } from "@/components/skeletons/ProjectsListSkeleton";

type ProjectSearch = {
  tab: "documents" | "assistant";
};

const projectsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "projects",
  component: ProjectsRouteBoundary,
  pendingComponent: ProjectsPageSkeleton,
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(projectsListRouteQuery()),
});

function ProjectsRouteBoundary() {
  return (
    <RouteErrorBoundary fallback={(error) => <RouteErrorFallback error={error} message="Could not load matters." />} resetKey="projects">
      <Suspense fallback={<ProjectsPageSkeleton />}>
        <ProjectsPageClient />
      </Suspense>
    </RouteErrorBoundary>
  );
}

const projectRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "projects/$id",
  validateSearch: (search: Record<string, unknown>): ProjectSearch => ({
    tab: search.tab === "documents" ? "documents" : "assistant",
  }),
  loader: ({ context: { queryClient }, params: { id } }) =>
    ensureProjectRouteData(queryClient, id),
  pendingComponent: ProjectSkeleton,
  component: ProjectRouteBoundary,
});

function ProjectRouteBoundary() {
  const { id } = projectRoute.useParams();
  return (
    <RouteErrorBoundary fallback={(error) => <RouteErrorFallback error={error} message="Could not load matter." />} resetKey={id}>
      <Suspense fallback={<ProjectSkeleton />}>
        <ProjectRouteWrapper />
      </Suspense>
    </RouteErrorBoundary>
  );
}

function ProjectRouteWrapper() {
  const { id } = projectRoute.useParams();
  return <ProjectPageClient projectId={id} />;
}

const projectAssistantRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "projects/$id/assistant",
  component: ProjectAssistantWrapper,
});

function ProjectAssistantWrapper() {
  const { id } = projectAssistantRoute.useParams();
  return (
    <ChatSurface
      projectId={id}
      landingTitle="Matter assistant"
      landingSubtitle="Ask against this matter's documents, versions, and context."
      heroHeading="What should Vadium do in this matter?"
      heroDescription="Attach matter documents or reference them directly in the request."
    />
  );
}

const projectAssistantChatRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "projects/$id/assistant/chat/$chatId",
  loader: ({ context: { queryClient }, params: { chatId } }) =>
    queryClient.ensureInfiniteQueryData(chatInfiniteRouteQuery(chatId)),
  pendingComponent: ChatSkeleton,
  component: ProjectChatRouteBoundary,
});

function ProjectChatRouteBoundary() {
  const { chatId } = projectAssistantChatRoute.useParams();
  return (
    <RouteErrorBoundary fallback={(error) => <RouteErrorFallback error={error} message="Could not load chat." />} resetKey={chatId}>
      <Suspense fallback={<ChatSkeleton />}>
        <ProjectChatWrapper />
      </Suspense>
    </RouteErrorBoundary>
  );
}

function ProjectChatWrapper() {
  const { id, chatId } = projectAssistantChatRoute.useParams();
  const { setCurrentChatId } = useChatHistory();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useSuspenseInfiniteQuery(chatInfiniteRouteQuery(chatId));
  useEffect(() => { setCurrentChatId(chatId); }, [chatId, setCurrentChatId]);
  const pages = data.pages;
  const newestPage = pages[0];
  const rows = [...pages].reverse().flatMap((page) => page.messages);
  return <ChatSurface chatId={chatId} projectId={id} initialTitle={newestPage?.chat.title} sourceDocumentId={newestPage?.chat.source_document_id} initialRows={rows} initialLive={newestPage?.live} hasOlderMessages={!!hasNextPage} isLoadingOlderMessages={isFetchingNextPage} onLoadOlderMessages={() => void fetchNextPage()} />;
}

// ---------------------------------------------------------------------------
// Account & Settings (reuse existing page components)
// ---------------------------------------------------------------------------

import AccountPage from "@/app/(pages)/account/page";
import OrganizationSettingsPage from "@/app/(pages)/settings/organization/page";
import OrgMembersPage from "@/app/(pages)/settings/organization/members/page";

const accountRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "account",
  component: AccountPage,
});

const orgSettingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "settings/organization",
  component: OrganizationSettingsPage,
});

const orgMembersRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "settings/organization/members",
  component: OrgMembersPage,
});

// ---------------------------------------------------------------------------
// Additional imports used by inline wrapper components
// ---------------------------------------------------------------------------

import { Suspense, useEffect } from "react";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { useChatHistory } from "@/contexts/ChatHistoryContext";

// ---------------------------------------------------------------------------
// Route tree & router
// ---------------------------------------------------------------------------

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  authenticatedRoute.addChildren([
    assistantRoute,
    chatsRoute,
    assistantChatRoute,
    projectsRoute,
    projectRoute,
    projectAssistantRoute,
    projectAssistantChatRoute,
    accountRoute,
    orgSettingsRoute,
    orgMembersRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { queryClient: undefined! },
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
